/* eslint no-param-reassign: ["error", { "props": false }] */
import * as React from 'react';
import PropTypes from 'prop-types';

import { Button } from 'antd';
import { cloneDeep, difference, noop } from 'lodash';

import TreeList from 'shared/components/Tree/TreeList';
import { getIntersectionListByUserRole, saveIntersection } from 'Root/http';
import { unflattenEntities, updateTreeData } from 'shared/components/Tree/treeUtils';

import TreeNode from './TreeNode';
import s from './GrantTree.pcss';
import Loader from '../../../../../shared/components/Loader/Loader';


function setIndentTree(tree) {
  return tree.map(node => ({ ...node, indent: node.weightIndex * 20 }));
}

function getTreeWithSelectedChildren(tree, selected) {
  const treeWithChildren = unflattenEntities(tree, 'id', 'parentSectionId');
  const checkChildren = (section, parent) => {
    if (section.children && section.children.length > 0) {
      section.children.forEach(child => checkChildren(child, section));
    }
    section.childrenSelected = section.children
      ? !section.children.some(c => !c.childrenSelected)
      : selected.some(sel => section.id === sel);
    if (parent && !parent.hasSelectedChildren) {
      parent.hasSelectedChildren =
        selected.some(sel => section.id === sel) || section.hasSelectedChildren;
    }
  };
  treeWithChildren.forEach(section => checkChildren(section));
  return treeWithChildren;
}

function getChildren(sec) {
  const res = [];
  const pushChildren = (section) => {
    if (section.children && section.children.length > 0) {
      section.children.forEach(child => pushChildren(child));
    }
    res.push(section);
  };
  pushChildren(sec);
  return res;
}

function getDifference(originalSelected = [], selected) {
  return {
    added: difference(selected, originalSelected),
    deleted: difference(originalSelected, selected),
  };
}

export default class GrantTree extends React.Component {
  static propTypes = {
    treeData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    userId: PropTypes.string.isRequired,
    roleId: PropTypes.string,
    disabled: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    roleId: undefined,
  };

  state = {
    treeData: this.props.treeData,
    selected: [],
    originalSelected: [],
    saving: false,
  };

  componentDidMount() {
    const { userId, roleId } = this.props;
    this.loadData(userId, roleId);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.treeData.length !== nextProps.treeData.length) {
      this.setState({ treeData: nextProps.treeData });
    }
    if (this.props.roleId !== nextProps.roleId) {
      this.loadData(nextProps.userId, nextProps.roleId);
    }
  }

  loadData = (userId, roleId) => {
    if (roleId) {
      getIntersectionListByUserRole(userId, roleId)
        .then((data) => {
          const selected = data.map(section => section.sectionId.id);
          this.setState({ selected, originalSelected: cloneDeep(selected) });
        });
    }
  };

  onExpandTree = (node) => {
    const { treeData } = this.state;
    let expandedId = node.id;
    const expandedKeys = treeData.filter(item => item.isExpanded).map(item => item.id);
    if (expandedKeys.includes(node.id)) {
      if (node.parentSectionId === 'null') {
        expandedId = undefined;
      } else {
        expandedId = node.parentSectionId;
      }
    }
    const updatedData = updateTreeData(treeData, expandedId, 'parentSectionId');
    this.setState({ treeData: updatedData });
  };

  checkSection = (node) => {
    const { selected } = this.state;
    const isSelected = selected.some(sel => sel === node.id);
    let newSelected = selected;
    const children = getChildren(node);
    if (isSelected) {
      newSelected = selected.filter(sel => !children.some(child => child.id === sel));
    } else {
      children.forEach((child) => {
        const isExist = selected.some(sel => sel === child.id);
        if (!isExist) {
          newSelected.push(child.id);
        }
      });
    }
    this.setState({ selected: newSelected });
  };

  checkChildrenTree = (tree) => {
    const { selected } = this.state;
    return getTreeWithSelectedChildren(tree, selected);
  };

  onCancel = () => this.setState({ selected: cloneDeep(this.state.originalSelected) });
  onSave = () => {
    const { userId, roleId } = this.props;
    const currentSelected = cloneDeep(this.state.selected);
    const diff = getDifference(this.state.originalSelected, currentSelected);
    const body = {
      userId,
      roleId,
      addSectionArray: diff.added.join(','),
      deleteSectionArray: diff.deleted.join(','),
    };
    this.setState({ saving: true }, () => {
      saveIntersection(body)
        .then(() => this.setState({ originalSelected: currentSelected, saving: false }));
    });
  };

  renderTreeNode = (node) => {
    const selected = this.state.selected.some(sel => sel === node.id);
    const disabled = !this.props.roleId || this.props.disabled;
    return (
      <TreeNode
        node={node}
        disabled={disabled}
        selected={selected}
        hasSelectedChildren={node.hasSelectedChildren}
        childrenSelected={node.childrenSelected}
        onClick={() => this.onExpandTree(node)}
        onSelect={() => this.checkSection(node)}
      />
    );
  };

  render() {
    const { treeData, selected, originalSelected, saving } = this.state;
    const expandedKeys = treeData.filter(item => item.isExpanded).map(item => item.id);
    const indentedTree = setIndentTree(treeData);
    const tree = unflattenEntities(indentedTree, 'id', 'parentSectionId');
    const withCheckedChildren = this.checkChildrenTree(tree);
    const diff = getDifference(originalSelected, selected);
    const diffCount = diff.added.length + diff.deleted.length;
    return (
      <div className={s.container}>
        {diffCount === 0 &&
          <div className={s.infoContainer}>
            <div className={s.label}>Дерево разделов</div>
          </div>
        }
        {diffCount > 0 &&
          <div className={s.diffContainer}>
            <div className={s.diffValue}>Внесено изменений: {diffCount}</div>
            <div className={s.controls}>
              <Button className={s.cancelBtn} onClick={this.onCancel} disabled={saving}>
                Отменить
              </Button>
              <Button type="primary" className={s.successBtn} onClick={this.onSave} disabled={saving}>
                {saving
                  ? <Loader fullScreen={false} size={20} />
                  : 'Сохранить изменения'
                }
              </Button>
            </div>
          </div>
        }
        <div className={s.treeContainer}>
          <div className={s.treeInnerContainer}>
            <TreeList
              tree={withCheckedChildren}
              expandedKeys={expandedKeys}
              selectedKeys={selected}
              onExpand={noop}
              customTreeNode
              withTooltip={false}
              treeNode={node => this.renderTreeNode(node)}
            />
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Root/pages/Users/UserPage/GrantTree/GrantTree.js